import { useApp, useTick } from "@pixi/react"
import type { DisplayObject } from "pixi.js"
import { useCallback, useEffect, useRef } from "react"
import { NODE_ANIMATION_DURATION } from "@/domains/visualization/constants/graph"
import type { PositionedNode } from "@/domains/visualization/types/Graph"
import {
  type AnimationMap,
  detectNodeChanges,
  initializeAnimations,
  updateNodePosition
} from "./useNodeAnimation.utils"

/**
 * A hook that manages smooth animations for nodes in a graph visualization.
 * It handles both position updates for existing nodes and animations for new nodes.
 *
 * The hook uses Pixi.js DisplayObjects directly for smooth animations:
 * - Updates DisplayObject positions directly
 * - Uses PIXI.js ticker for smooth frame updates
 * - Handles both moved and new nodes
 *
 * @param positionedNodes - Current state of all nodes with positions
 * @param currentNodeInstance - Current node instance for new node animations
 * @param layoutReady - Whether the graph layout is ready for animation
 * @param stage - The Pixi.js stage/container that contains the nodes
 */

export const useNodeAnimation = (
  positionedNodes: PositionedNode[],
  currentNodeInstance: DisplayObject | null,
  layoutReady: boolean
) => {
  const { stage } = useApp()
  // Ref to store the previous state of nodes for comparison
  const previousNodesRef = useRef<PositionedNode[]>([])

  // Ref to store active animations
  const activeAnimationsRef = useRef<AnimationMap>(new Map())

  useEffect(() => {
    if (layoutReady) {
      const changes = detectNodeChanges(positionedNodes, previousNodesRef.current)
      const newAnimations = initializeAnimations(changes, stage, previousNodesRef.current, currentNodeInstance)

      if (newAnimations.size > 0) {
        activeAnimationsRef.current = newAnimations
      }

      previousNodesRef.current = positionedNodes
    }
  }, [positionedNodes, layoutReady, currentNodeInstance, stage])

  useTick(
    useCallback(() => {
      if (activeAnimationsRef.current.size === 0) {
        return
      }

      const currentTime = performance.now()
      const completedAnimations: string[] = []

      // Update each active animation
      for (const [nodeId, animation] of activeAnimationsRef.current) {
        const completed = updateNodePosition(animation, currentTime, NODE_ANIMATION_DURATION)

        if (completed) {
          completedAnimations.push(nodeId)
        }
      }

      // Clean up completed animations
      for (const nodeId of completedAnimations) {
        activeAnimationsRef.current.delete(nodeId)
      }
    }, [])
  )
}
